import {Search} from '@mui/icons-material';
import {debounce, OutlinedInput} from '@mui/material';
import {ChangeEvent, FC, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {AMPFormSelect} from 'src/components/common/AMPFormSelect';
import {FilterBar, FilterBarField} from 'src/components/common/FilterBar';
import {ChargerExportButton} from 'src/components/chargers/ChargerExportButton';
import {ChargePointStatus, ConnectorStatusEnum} from 'src/enums';
import {searchInputSx} from 'src/components/tables/tableToolbar.sx';
import {ChargerFilterValue, ChargerTableActionsType} from './ChargerTableActions.types';

const ALL = '';

/**
 * The chargers toolbar.
 *
 * Same move as the locations one: the filters come out of the popover and apply as they change. The
 * country and city fields the old form declared were never rendered — nothing passed them — so they
 * are not carried over; what is here is what the screen actually filtered by.
 *
 * Controlled: the dropdowns render the filter the list is on, and a change reports only the field
 * that changed. They used to hold a copy of their own, which nothing told about a status set
 * elsewhere - so on the chargers page, where the strip's tiles set exactly that, a dropdown read
 * "all" over a filtered list and the next keystroke in the search box sent the stale copy back.
 */
export const ChargerTableActions: FC<ChargerTableActionsType> = ({
                                                                    handleSearch,
                                                                    handleFilter,
                                                                    filters,
                                                                    title,
                                                                    icon,
                                                                    onAdd,
                                                                    addLabel,
                                                                    totalElements,
                                                                }) => {
    const intl = useIntl();
    const chargePointStatus = filters.chargePointStatus ?? ALL;
    const connectorStatus = filters.connectorStatus ?? ALL;

    // Only what changed: the table merges it onto the filter the list is on, so the other dropdown
    // keeps its value without this component having to remember it.
    const apply = (next: ChargerFilterValue) => handleFilter(next);

    const stationOptions = useMemo(() => [
        {id: 0, name: intl.formatMessage({id: 'allStatuses'}), value: ALL},
        ...Object.values(ChargePointStatus).map((status, index) => ({
            id: index + 1,
            name: status,
            value: status,
        })),
    ], [intl]);

    const connectorOptions = useMemo(() => [
        {id: 0, name: intl.formatMessage({id: 'allConnectors'}), value: ALL},
        ...Object.values(ConnectorStatusEnum).map((status, index) => ({
            id: index + 1,
            name: status,
            value: status,
        })),
    ], [intl]);

    const fields: FilterBarField[] = [
        {
            id: 'stationStatus',
            control: (
                <AMPFormSelect
                    dense
                    data={stationOptions}
                    value={chargePointStatus}
                    input="status"
                    onChange={(e) => apply({chargePointStatus: String(e.target.value)})}
                />
            ),
        },
        {
            id: 'connectorStatus',
            control: (
                <AMPFormSelect
                    dense
                    data={connectorOptions}
                    value={connectorStatus}
                    input="status"
                    onChange={(e) => apply({connectorStatus: String(e.target.value)})}
                />
            ),
        },
    ];

    return (
        <FilterBar
            title={title}
            icon={icon}
            onAdd={onAdd}
            addLabel={addLabel}
            subtitle={typeof totalElements === 'number'
                ? intl.formatMessage({id: 'rowsFound'}, {count: totalElements})
                : undefined}
            fields={fields}
            hasActiveFilters={Boolean(chargePointStatus || connectorStatus)}
            onClearAll={() => apply({chargePointStatus: ALL, connectorStatus: ALL})}
            actions={(
                <>
                    <OutlinedInput
                        sx={searchInputSx}
                        color="secondary"
                        type="text"
                        placeholder={intl.formatMessage({id: 'search'})}
                        name="search"
                        // Uncontrolled for the debounce, but seeded: the box has to say what the list is
                        // filtered by after a Back, which remounts it with the keyword still in the URL.
                        defaultValue={filters?.keyword ?? ''}
                        onChange={(event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
                            debounce(() => handleSearch(event.target.value), 1000)()}
                        startAdornment={<Search/>}
                    />
                    <ChargerExportButton filters={filters}/>
                </>
            )}
        />
    );
};
